import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useNumberFormat } from "@/shared/hooks/use-number-format";
import { useLanguage } from "@/shared/hooks/use-language";
import { formatAmountScale, formatCount } from "@/shared/lib/amount";
import { formatDateInFilterZone } from "@/shared/lib/date-utils";
import { isUnreadableResponseError } from "@/shared/lib/response-shape";
import { consolidationStateOf, type PositionSummary } from "@/features/portfolio/api/position";
import { isValuationHistoryTooLongError, type PortfolioValuation } from "@/features/portfolio/api/portfolio-valuation";
import {
  NAV_HISTORY_PERIODS,
  type NavHistoryPeriod,
  type NavHistorySeries,
} from "@/features/portfolio/hooks/use-portfolio-valuation";

const LINE = "hsl(var(--chart-1))";
const GRID = "hsl(var(--border))";
const AXIS_TEXT = "hsl(var(--muted-foreground))";
const SURFACE = "hsl(var(--card))";
const CHART_HEIGHT = 240;
const AXIS_FONT_SIZE = 11;
const Y_AXIS_MIN_WIDTH = 40;
const Y_AXIS_TICK_PADDING = 12;
const FALLBACK_CHARACTER_WIDTH = 7;
const DEFAULT_NAV_SCALE = 2;

let measuringContext: CanvasRenderingContext2D | null | undefined;

function yAxisWidthFor(labels: string[]): number {
  if (measuringContext === undefined) {
    measuringContext = document.createElement("canvas").getContext("2d");
  }
  const context = measuringContext;
  if (context) {
    context.font = `${AXIS_FONT_SIZE}px ${getComputedStyle(document.body).fontFamily}`;
  }
  const widest = labels.reduce(
    (width, label) => Math.max(width, context ? context.measureText(label).width : label.length * FALLBACK_CHARACTER_WIDTH),
    0,
  );
  return Math.max(Y_AXIS_MIN_WIDTH, Math.ceil(widest) + Y_AXIS_TICK_PADDING);
}

type PartialReason = "unpriced" | "rate-missing" | "nothing-priced";

/**
 * One day on the chart. `value` is the only float here, and it exists for geometry alone:
 * what the reader reads is `navAmount`/`navScale`, formatted exactly. A day whose total
 * did not resolve keeps `value: null`, which draws as a gap the tooltip still explains.
 * `liveValue` carries the dashed segment: set on the last plotted day and on the "now"
 * point, null everywhere else, so the second area draws only that one stretch — and
 * connects across the nulls between them, since an unresolved day after the last plotted
 * one would otherwise break the segment before it reaches "now".
 */
interface NavHistoryPoint {
  time: number;
  referenceTime: string;
  currency: string;
  navAmount: number | null;
  navScale: number | null;
  value: number | null;
  liveValue: number | null;
  partialReason: PartialReason | null;
  unpricedCount: number;
  isNow: boolean;
}

/**
 * `seriesCurrency` is the currency the whole read was made in — the fallback label for a
 * day whose own `consolidated_nav` is absent, since that day carries no currency of its
 * own to read.
 *
 * Every day the backend returned keeps its place here, plotted or not: a day with no
 * consolidated total (missing `consolidated_nav`, a total this runtime cannot represent,
 * or nothing priced) still carries `value: null` and returns, rather than being dropped.
 * Dropping the day would leave nothing for the chart's `connectNulls={false}` to break on,
 * and the line would draw straight across a span the portfolio may have held nothing in.
 * Only a row whose `reference_time` cannot be parsed into a point on the axis at all is
 * left out — there is no x position to place it at.
 */
function pointOf(valuation: PortfolioValuation, seriesCurrency: string): NavHistoryPoint | null {
  const time = new Date(valuation.reference_time).getTime();
  if (Number.isNaN(time)) return null;
  const consolidated = valuation.consolidated_nav;
  const state = consolidationStateOf(valuation);
  const unpricedCount = Number.isInteger(valuation.unpriced_position_count)
    ? valuation.unpriced_position_count
    : 0;
  const isPlottable = state === "consolidated";
  const partialReason: PartialReason | null = state === "rate-missing"
    ? "rate-missing"
    : state === "nothing-priced"
      ? "nothing-priced"
      : unpricedCount > 0
        ? "unpriced"
        : null;
  return {
    time,
    referenceTime: valuation.reference_time,
    currency: consolidated?.currency ?? seriesCurrency,
    navAmount: isPlottable && consolidated ? consolidated.nav_amount : null,
    navScale: isPlottable && consolidated ? consolidated.nav_scale : null,
    value: isPlottable && consolidated?.nav_amount != null
      ? Number(consolidated.nav_amount) / 10 ** consolidated.nav_scale
      : null,
    liveValue: null,
    partialReason,
    unpricedCount,
    isNow: false,
  };
}

/**
 * The live summary as the series' last, provisional point: today's figure before the day
 * is valued. Appended only when the summary is a consolidated total in the series' own
 * currency and there is a plotted day for the dashed segment to start from. It is never
 * marked partial — the summary's own card already carries that qualification.
 */
function nowPointOf(summary: PositionSummary | null, currency: string): NavHistoryPoint | null {
  const consolidated = summary?.consolidated_nav;
  if (consolidated == null || consolidated.nav_amount == null) return null;
  if (consolidationStateOf(summary) !== "consolidated") return null;
  if (consolidated.currency !== currency) return null;
  const value = Number(consolidated.nav_amount) / 10 ** consolidated.nav_scale;
  return {
    time: Date.now(),
    referenceTime: "",
    currency: consolidated.currency,
    navAmount: consolidated.nav_amount,
    navScale: consolidated.nav_scale,
    value: null,
    liveValue: value,
    partialReason: null,
    unpricedCount: 0,
    isNow: true,
  };
}

interface TooltipEntry {
  payload?: NavHistoryPoint;
}

const NavHistoryTooltip = ({ active, payload }: { active?: boolean; payload?: TooltipEntry[] }) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();
  const { numberFormat } = useNumberFormat();
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const day = point.isNow ? t("portfolio.navHistory.now") : formatDateInFilterZone(point.referenceTime, dateFormat);
  const reason = point.partialReason === "unpriced"
    ? t("portfolio.navHistory.partialUnpriced").replace("{count}", formatCount(point.unpricedCount, numberFormat))
    : point.partialReason === "rate-missing"
      ? t("portfolio.navHistory.partialRateMissing")
      : point.partialReason === "nothing-priced"
        ? t("portfolio.navHistory.partialNothingPriced")
        : null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md" data-testid="nav-history-tooltip">
      <p className="text-muted-foreground">{day}</p>
      <p className="font-semibold tabular-nums">
        {formatAmountScale(point.navAmount, point.navScale, numberFormat)} {point.currency}
      </p>
      {reason && <p className="mt-1 max-w-56 text-muted-foreground">{reason}</p>}
    </div>
  );
};

interface DotProps {
  cx?: number;
  cy?: number;
  index?: number;
  payload?: NavHistoryPoint;
}

// A hollow marker on a plotted day whose figure is incomplete. Every other day draws no
// marker at all: a year is some 250 points, and a dot on each would bury the line.
const renderPartialDot = ({ cx, cy, index, payload }: DotProps) => {
  if (cx == null || cy == null || payload == null || payload.value == null || payload.partialReason == null) {
    return <g key={`dot-${index}`} />;
  }
  return (
    <circle
      key={`dot-${index}`}
      cx={cx}
      cy={cy}
      r={4}
      fill={SURFACE}
      stroke={LINE}
      strokeWidth={2}
      data-testid="nav-history-partial-point"
    />
  );
};

const renderNowDot = ({ cx, cy, index, payload }: DotProps) => {
  if (cx == null || cy == null || payload == null || !payload.isNow) {
    return <g key={`live-dot-${index}`} />;
  }
  return (
    <circle key={`live-dot-${index}`} cx={cx} cy={cy} r={3} fill={LINE} data-testid="nav-history-now-point" />
  );
};

interface NavHistoryChartProps {
  series: NavHistorySeries | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  period: NavHistoryPeriod;
  onPeriodChange: (period: NavHistoryPeriod) => void;
  currency: string;
  summary: PositionSummary | null;
  /** Whether the figures on this page are final yet — see `useBookingStatus`. The chart
   *  waits on the same gate `NavHeader` applies, so it never draws before the card above
   *  it has shown its own figure. */
  awaitsBooking: boolean;
}

export const NavHistoryChart = ({
  series, isLoading, isFetching, isError, error, onRetry,
  period, onPeriodChange, currency, summary, awaitsBooking,
}: NavHistoryChartProps) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();
  const { numberFormat } = useNumberFormat();

  const { points, plottedPoints, realPointCount, hasPartial, navScale } = useMemo(() => {
    const realPoints = (series?.valuations ?? [])
      .flatMap((valuation) => {
        const point = pointOf(valuation, series?.currency ?? currency);
        return point ? [point] : [];
      })
      .sort((a, b) => a.time - b.time);
    const plotted = realPoints.filter((point) => point.value != null);
    const lastPlotted = plotted[plotted.length - 1];
    const now = series && lastPlotted ? nowPointOf(summary, series.currency) : null;
    if (now && lastPlotted) {
      lastPlotted.liveValue = lastPlotted.value;
    }
    const scale = plotted.reduce(
      (widest, point) => Math.max(widest, point.navScale ?? 0),
      plotted.length > 0 ? 0 : DEFAULT_NAV_SCALE,
    );
    return {
      points: now ? [...realPoints, now] : realPoints,
      plottedPoints: plotted,
      realPointCount: realPoints.length,
      hasPartial: realPoints.some((point) => point.partialReason != null),
      navScale: scale,
    };
  }, [series, summary, currency]);

  // A series can be stale for two different reasons, and both need the same banner. The
  // read for this exact key can fail (`isError`) while an earlier good read for the same
  // key is shown instead; or the read for this key can simply not have landed yet —
  // including a paused or disabled fetch, which sets neither `isLoading` nor `isError` —
  // while a different key's series (a different period or currency) is shown in the
  // meantime. Gating the banner on `isError` alone left the second case rendering that
  // other key's chart under this key's pressed button with nothing on screen saying so.
  const isFatal = isError && series == null;
  const showsSkeleton = !isFatal && (awaitsBooking || isLoading);
  const isKeyMismatch = series != null && (series.period !== period || series.currency !== currency);
  const isRetained = series != null && (isError || isKeyMismatch);
  const isRetainedFromAnotherKey = isKeyMismatch;
  const fatalMessage = isUnreadableResponseError(error)
    ? t("errors.unreadableResponse")
    : isValuationHistoryTooLongError(error)
      ? t("portfolio.navHistory.tooLong")
      : t("portfolio.navHistory.loadFailed");
  const retainedMessage = isRetainedFromAnotherKey && series
    ? t("portfolio.navHistory.refreshFailed")
      .replace("{period}", period)
      .replace("{currency}", currency)
      .replace("{servedPeriod}", series.period)
      .replace("{servedCurrency}", series.currency)
    : t("portfolio.navHistory.staleNote");

  const firstPoint = plottedPoints.length > 0 ? points[0] : null;
  const lastReal = realPointCount > 0 ? points[realPointCount - 1] : null;
  const lastPlotted = plottedPoints[plottedPoints.length - 1] ?? null;
  const formatDay = (time: number) => formatDateInFilterZone(new Date(time).toISOString(), dateFormat);
  const formatTick = (tick: number) => formatAmountScale(Math.round(tick * 10 ** navScale), navScale, numberFormat);
  const plottedValues = plottedPoints.flatMap((point) => (point.value == null ? [] : [point.value]));
  const lowestValue = Math.min(0, ...plottedValues);
  const highestValue = Math.max(0, ...plottedValues);
  const largestMagnitude = Math.max(Math.abs(lowestValue), highestValue, 1);
  const niceCeiling = 10 ** Math.max(1, Math.ceil(Math.log10(largestMagnitude)));
  const yAxisWidth = yAxisWidthFor([
    formatTick(highestValue),
    formatTick(lowestValue),
    formatTick(lowestValue < 0 ? -niceCeiling : niceCeiling),
  ]);

  return (
    <Card className="mt-4 p-6" data-testid="nav-history-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 className="font-semibold">{t("portfolio.navHistory.title")}</h3>
        <div
          className="flex flex-wrap rounded-md border border-border text-xs"
          role="group"
          aria-label={t("portfolio.navHistory.period")}
          data-testid="nav-history-period"
        >
          {NAV_HISTORY_PERIODS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onPeriodChange(option)}
              aria-pressed={option === period}
              className={`min-h-8 px-3 py-1.5 ring-offset-background transition-colors first:rounded-l-md last:rounded-r-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                option === period
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        {showsSkeleton ? (
          <div className="skeleton-shimmer rounded bg-muted" style={{ height: CHART_HEIGHT }} data-testid="nav-history-loading" />
        ) : isFatal ? (
          <div className="rounded-md border border-destructive p-4" data-testid="nav-history-error">
            <p className="text-destructive mb-3 text-sm">{fatalMessage}</p>
            <Button variant="outline" size="sm" onClick={onRetry} data-testid="nav-history-retry">
              {t("portfolio.retry")}
            </Button>
          </div>
        ) : (
          <div
            aria-busy={isFetching}
            className={`space-y-3 transition-opacity ${isFetching ? "opacity-60" : ""}`}
          >
            {isRetained && (
              <div className="flex flex-wrap items-center gap-3" data-testid="nav-history-stale-error">
                <p className="text-sm text-destructive">{retainedMessage}</p>
                <Button variant="outline" size="sm" onClick={onRetry} data-testid="nav-history-stale-retry">
                  {t("portfolio.retry")}
                </Button>
              </div>
            )}
            {series != null && series.valuations.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="nav-history-empty">
                {series.period === "ALL" ? t("portfolio.navHistory.empty") : t("portfolio.navHistory.emptyPeriod")}
              </p>
            ) : series != null && plottedPoints.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="nav-history-no-total">
                {t("portfolio.navHistory.noTotal")}
              </p>
            ) : series != null && lastPlotted != null ? (
              <>
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
                  <p>
                    <span className="text-muted-foreground">{t("portfolio.navHistory.lastValue")}: </span>
                    <span className="font-semibold tabular-nums" data-testid="nav-history-last-value">
                      {formatAmountScale(lastPlotted.navAmount, lastPlotted.navScale, numberFormat)} {lastPlotted.currency}
                    </span>
                  </p>
                  {firstPoint && lastReal && realPointCount >= 2 && (
                    <p>
                      <span className="text-muted-foreground">{t("portfolio.navHistory.range")}: </span>
                      <span className="tabular-nums" data-testid="nav-history-range">
                        {formatDay(firstPoint.time)} – {formatDay(lastReal.time)}
                      </span>
                    </p>
                  )}
                  <p>
                    <span className="text-muted-foreground">{t("portfolio.navHistory.points")}: </span>
                    <span className="tabular-nums" data-testid="nav-history-point-count">{formatCount(plottedPoints.length, numberFormat)}</span>
                  </p>
                </div>
                <div
                  className="w-full"
                  style={{ height: CHART_HEIGHT }}
                  data-testid="nav-history-chart"
                  role="img"
                  aria-label={`${t("portfolio.navHistory.title")}: ${formatAmountScale(lastPlotted.navAmount, lastPlotted.navScale, numberFormat)} ${lastPlotted.currency}`}
                >
                  {points.length < 2 ? (
                    // A line or an area needs two points to draw a path — recharts renders
                    // an empty canvas for one, which above an otherwise-populated "Last
                    // value" reads as broken rather than as the single day it is. A day
                    // this hollow still carries a reason, same as the partial marker below.
                    <div
                      className="flex h-full flex-col items-center justify-center gap-2"
                      data-testid="nav-history-single-point"
                    >
                      <span
                        aria-hidden="true"
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: LINE }}
                      />
                      <p className="text-xs text-muted-foreground">{t("portfolio.navHistory.singlePoint")}</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="time"
                          type="number"
                          scale="time"
                          domain={["dataMin", "dataMax"]}
                          tickFormatter={formatDay}
                          minTickGap={40}
                          tick={{ fill: AXIS_TEXT, fontSize: AXIS_FONT_SIZE }}
                          stroke={GRID}
                          tickLine={false}
                        />
                        <YAxis
                          domain={["auto", "auto"]}
                          width={yAxisWidth}
                          tickFormatter={formatTick}
                          tick={{ fill: AXIS_TEXT, fontSize: AXIS_FONT_SIZE }}
                          stroke={GRID}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip content={<NavHistoryTooltip />} filterNull={false} cursor={{ stroke: GRID }} />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke={LINE}
                          strokeWidth={2}
                          fill={LINE}
                          fillOpacity={0.15}
                          connectNulls={false}
                          dot={renderPartialDot}
                          activeDot={{ r: 4, fill: LINE, stroke: SURFACE }}
                          isAnimationActive={false}
                        />
                        <Area
                          type="linear"
                          dataKey="liveValue"
                          stroke={LINE}
                          strokeWidth={2}
                          strokeDasharray="4 4"
                          fill={LINE}
                          fillOpacity={0.05}
                          connectNulls
                          dot={renderNowDot}
                          activeDot={{ r: 4, fill: LINE, stroke: SURFACE }}
                          isAnimationActive={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div aria-live="polite" className="space-y-1 text-xs text-muted-foreground">
                  {hasPartial && (
                    <p className="flex items-center gap-2" data-testid="nav-history-partial-note">
                      <span
                        aria-hidden="true"
                        className="inline-block h-3 w-3 shrink-0 rounded-full border-2"
                        style={{ borderColor: LINE, backgroundColor: SURFACE }}
                      />
                      <span>{t("portfolio.navHistory.partialNote")}</span>
                    </p>
                  )}
                  {points.some((point) => point.isNow) && (
                    <p data-testid="nav-history-now-note">{t("portfolio.navHistory.nowNote")}</p>
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
};
